import {
  RECURRENCE_UNIT_HOURS,
  RecurrenceUnit,
  Task,
} from '../../shared/models/task.model';

type Severity =
  | 'success'
  | 'info'
  | 'warn'
  | 'danger'
  | 'secondary'
  | 'contrast';

export function getTaskStatusSeverity(
  status: string | null | undefined,
): Severity {
  switch (status) {
    case 'open':
      return 'info';
    case 'in_progress':
      return 'warn';
    case 'done':
      return 'secondary';
    case 'accepted':
      return 'success';
    default:
      return 'secondary';
  }
}

export function getPrioritySeverity(
  priority: string | null | undefined,
): Severity {
  switch (priority) {
    case 'low':
      return 'info';
    case 'medium':
      return 'warn';
    case 'high':
    case 'critical':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function getPriorityLabelKey(priority: string): string {
  return `defect.priorities.${priority}`;
}

/**
 * Translation key for the recurrence, used with `{ count: recurrence_every }`:
 * "Täglich" for an interval of one, "Alle 2 Tage" otherwise.
 */
export function getRecurrenceLabelKey(
  unit: RecurrenceUnit | string,
  every: number | null | undefined,
): string {
  return every === 1
    ? `task.recurrence.single.${unit}`
    : `task.recurrence.every.${unit}`;
}

/**
 * A task is finished once its current execution needs nothing more:
 * accepted, or done when no approval is required.
 */
export function isTaskFinished(task: Task): boolean {
  const status = task.current_execution?.status;
  return (
    status === 'accepted' || (status === 'done' && !task.requires_approval)
  );
}

const HOUR_MS = 3_600_000;
/** One-off tasks have no interval; they get the window of a daily task. */
const ONE_OFF_INTERVAL_HOURS = RECURRENCE_UNIT_HOURS.day;
/** The warning starts at least this many times the estimated effort ahead. */
const ESTIMATE_FACTOR = 2;

/**
 * How long before the due date a task counts as "due soon".
 * Grows sub-linearly with the interval: 10 min · (interval / 1 h)^0.75, so
 * hourly → 10 min, daily → ~1.8 h, every 2 days → ~3 h, weekly → ~8 h,
 * monthly → ~23 h. Never shorter than twice the estimated effort, so there is
 * still time to do it.
 */
export function getDueSoonWindowMs(task: Task): number {
  const intervalHours =
    task.is_recurring && task.recurrence_unit
      ? RECURRENCE_UNIT_HOURS[task.recurrence_unit as RecurrenceUnit] *
        (task.recurrence_every ?? 1)
      : ONE_OFF_INTERVAL_HOURS;
  const byInterval = 10 * 60_000 * Math.pow(intervalHours, 0.75);
  const byEstimate = (task.estimated_time ?? 0) * ESTIMATE_FACTOR * HOUR_MS;
  return Math.max(byInterval, byEstimate);
}
